import { isSupportedNormalizedVersion, PROTOCOL_VERSION } from '@borgee/agent-remote-protocol';
import { bindApplicationRequestSocket } from '../lib/application-requests';
import {
  syncNormalizedActivity,
  resumeSessionObservationChannel,
} from '../lib/session-observations';
import { dshSessionKey } from '../lib/dsh-session-journal';
import {
  bindSessionObservationSocket,
  recoverSessionTimeline,
  releaseSessionObservationChannel,
} from '../lib/session-observations';
import {
  validSessionAvailability,
  validTimelineReplacement,
  validTimelineRecoveryResult,
  unwrapNormalizedApplicationTransport,
} from '@borgee/plugin-sdk/bpp-normalized';
import { msg } from '@lingui/core/macro';
import {
  NormalizedFragmentAssembler,
  normalizedTransportError,
  type NormalizedTransportFailure,
  type NormalizedTransportFrame,
} from '@borgee/plugin-sdk/bpp-normalized';
import { normalizeActivityFrame } from '../lib/normalized-activity';
import { useEffect, useRef, useCallback } from 'react';
import { useToast } from '../components/Toast';
import { getDevUserId, requestAgentApplication } from '../lib/api';
import {
  disconnectMessageCache,
  recordMessageReceipt,
  rejectMessageViewer,
} from '../lib/message-cache-session';
import type { ConnectionState, Message, Channel, PendingMessage } from '../types';
import { useAuthStore } from '../stores/useAuthStore';
import {
  captureChannelContext,
  captureChannelRevision,
  invalidateChannelScopes,
  useChannelStore,
  waitForChannelSnapshot,
} from '../stores/useChannelStore';
import { channelRosterAdmission, useChannelMemberStore } from '../stores/useChannelMemberStore';
import { useMessageStore } from '../stores/useMessageStore';
import { useDmStore } from '../stores/useDmStore';
import { usePresenceStore } from '../stores/usePresenceStore';
import {
  onMessageReceived,
  onMessageAcked,
  heldMessagesAtGapEnd,
  resyncAfterReconnect,
  type HeldMessages,
} from '../stores/actions';
import { useTypingStore } from '../stores/useTypingStore';
import { useThreadStore } from '../stores/useThreadStore';
import { useTaskStore } from '../stores/useTaskStore';
import { useServerStore } from '../stores/useServerStore';
import { useAgentActivityStore } from '../stores/useAgentActivityStore';
import { useNormalizedActivityStore } from '../stores/useNormalizedActivityStore';
import { useDshSessionStore } from '../stores/useDshSessionStore';
import {
  isDirectMessageSessionScope,
  resolveNormalizedSessionOwner,
} from '../lib/normalized-session-scope';
import { useTaskRunStore } from '../stores/useTaskRunStore';
import { useAgentStore } from '../stores/useAgentStore';
import {
  dispatchInvitationPending,
  dispatchInvitationDecided,
  dispatchMentionPushed,
} from './useWsHubFrames';
import type {
  AgentInvitationPendingFrame,
  AgentInvitationDecidedFrame,
  MentionPushedFrame,
  AgentActivityFrame,
  AgentTaskStateChangedFrame,
  GuildMembersChangedFrame,
  ZonesChangedFrame,
} from '../types/ws-frames';
import { markPresence } from './usePresence';
import { markRT3Presence } from './useRT3Presence';
import { useActivityHeartbeat } from './useActivityHeartbeat';
import type { AgentRuntimeReason, AgentRuntimeState } from '../lib/api';
import { reduceRealtimeFrame } from '../client-core/realtime';
import { bindInteractionResponseSocket } from '../lib/interaction-responses';
import { notifyExecutionUsageUpdated } from '../lib/execution-usage-updates';

const PING_INTERVAL = 25_000;
const DEFAULT_RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const E2E_RECONNECT_DELAY_RAW = import.meta.env.VITE_E2E_WS_RECONNECT_DELAY_MS;
const E2E_RECONNECT_DELAY = Number(E2E_RECONNECT_DELAY_RAW);
const RECONNECT_DELAYS =
  import.meta.env.DEV &&
  E2E_RECONNECT_DELAY_RAW !== undefined &&
  E2E_RECONNECT_DELAY_RAW !== '' &&
  Number.isFinite(E2E_RECONNECT_DELAY) &&
  E2E_RECONNECT_DELAY >= 0
    ? [E2E_RECONNECT_DELAY]
    : DEFAULT_RECONNECT_DELAYS;
const AUTH_FAILURE_CODES = new Set([4001, 4003]);

// How long the reconnect repair waits before each further attempt. The schedule
// is short and finite: it exists to ride out a REST rail that is briefly still
// refusing reads on a socket that is already back, not to keep a permanently
// broken repair running.
const RESYNC_RETRY_DELAYS = [1000, 3000, 9000];

interface ReconnectRepair {
  ws: WebSocket;
  heldAtGapEnd: HeldMessages;
  listedBefore: ReadonlySet<string>;
  learnedMessages: Map<string, Map<string, Message>>;
  sameContext: () => boolean;
  sameRevision: () => boolean;
  running: boolean;
}

/** Socket creation waits for the authenticated session cookie. */
export function useWebSocket(
  isAuthenticated = true,
  onAuthClose?: () => void,
  activeServerChannelId: string | null = null,
) {
  useActivityHeartbeat();
  const { showToast } = useToast();
  const wsRef = useRef<WebSocket | null>(null);
  const fragmentConnection = useRef<{
    assembler: NormalizedFragmentAssembler;
    dispose: () => void;
  }>();
  const interactionConnection = useRef<ReturnType<typeof bindInteractionResponseSocket>>();
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const pingTimer = useRef<ReturnType<typeof setInterval>>();
  const subscribedChannels = useRef<Set<string>>(new Set());
  const sessionSubscriptionScopes = useRef(
    new Map<string, { accountId: string; guildId?: string }>(),
  );
  const pendingActivityRecovery = useRef(new Map<string, Map<string | undefined, () => void>>());
  const authenticatedRef = useRef(isAuthenticated);
  authenticatedRef.current = isAuthenticated;
  const mountedRef = useRef(true);
  const scheduleReconnectRef = useRef<() => void>();
  const ackTimers = useRef<Map<string, () => void>>(new Map());
  const reconnectRepair = useRef<ReconnectRepair | null>(null);
  const resumeRepairRef = useRef<(job: ReconnectRepair) => Promise<void>>();
  const handleMessageRef = useRef<
    (data: { type: string; [key: string]: unknown }, verifiedViewer: boolean) => void
  >(() => {});
  const onAuthCloseRef = useRef(onAuthClose);
  onAuthCloseRef.current = onAuthClose;
  const activeServerChannelRef = useRef(activeServerChannelId);
  activeServerChannelRef.current = activeServerChannelId;

  const reconcileProjectMembership = useCallback(() => {
    return useServerStore.getState().reconcileMembership(useAuthStore.getState().guildId);
  }, []);

  // Use store selectors for reactive values
  const connectionState = usePresenceStore((s) => s.connectionState);
  const currentAccountId = useAuthStore((state) => state.currentUser?.id ?? null);
  const previousAccountId = useRef(currentAccountId);

  useEffect(
    () =>
      useAuthStore.subscribe((state, previous) => {
        if (
          state.currentUser?.id === previous.currentUser?.id &&
          state.guildId === previous.guildId
        )
          return;
        const sameAccount =
          state.currentUser?.id && state.currentUser.id === previous.currentUser?.id;
        for (const [channelId, scope] of sessionSubscriptionScopes.current) {
          if (
            sameAccount &&
            scope.guildId &&
            isDirectMessageSessionScope({ ...scope, guildId: scope.guildId })
          )
            continue;
          releaseSessionObservationChannel(channelId);
          sessionSubscriptionScopes.current.delete(channelId);
          pendingActivityRecovery.current.delete(channelId);
          fragmentConnection.current?.assembler.clearScope(channelId);
        }
        if (!sameAccount) fragmentConnection.current?.assembler.reset();
        useDshSessionStore.getState().resetMemory(sameAccount ? state.currentUser!.id : undefined);
      }),
    [],
  );

  const subscribedSessionOwner = useCallback((channelId: string) => {
    if (!authenticatedRef.current || !subscribedChannels.current.has(channelId)) return;
    const scope = resolveNormalizedSessionOwner(channelId);
    const subscription = sessionSubscriptionScopes.current.get(channelId);
    if (
      !scope ||
      subscription?.accountId !== scope.accountId ||
      (subscription.guildId && subscription.guildId !== scope.guildId)
    )
      return;
    if (!subscription.guildId) sessionSubscriptionScopes.current.set(channelId, scope);
    return scope;
  }, []);
  const syncSubscribedActivity = useCallback(
    async (channelId: string, agentId?: string): Promise<void> => {
      const socket = wsRef.current;
      const owner = subscribedSessionOwner(channelId);
      if (!socket || !owner) return;
      pendingActivityRecovery.current.get(channelId)?.delete(agentId);
      const subscription = sessionSubscriptionScopes.current.get(channelId);
      const roster = await useChannelMemberStore
        .getState()
        .loadChannelMembers(channelId)
        .catch(() => undefined);
      const isCurrent = () =>
        socket === wsRef.current &&
        sessionSubscriptionScopes.current.get(channelId) === subscription &&
        subscribedSessionOwner(channelId)?.accountId === owner.accountId;
      if (!isCurrent()) return;
      if (roster?.status !== 'ready') {
        if (roster?.status === 'unavailable') {
          // Subscription confirmation can arrive before the authorized thread roster.
          const pending =
            pendingActivityRecovery.current.get(channelId) ??
            new Map<string | undefined, () => void>();
          if (agentId !== undefined && pending.has(undefined)) return;
          if (agentId === undefined) pending.clear();
          const retry = () => {
            void Promise.resolve().then(() => {
              const pending = pendingActivityRecovery.current.get(channelId);
              if (pending?.get(agentId) !== retry) return;
              if (!isCurrent()) {
                pending.delete(agentId);
                return;
              }
              if (
                !channelRosterAdmission(channelId).available ||
                !useChannelMemberStore.getState().byChannelId[channelId]
              )
                return;
              pending.delete(agentId);
              void syncSubscribedActivity(channelId, agentId);
            });
          };
          pending.set(agentId, retry);
          pendingActivityRecovery.current.set(channelId, pending);
          retry();
        }
        return;
      }
      const profileEpoch = usePresenceStore.getState().providerOutputEpoch;
      await Promise.all(
        roster.members
          .filter((member) => member.role === 'agent' && (!agentId || member.user_id === agentId))
          .map(async (member) => {
            const profileRevision =
              usePresenceStore.getState().providerOutputRevisions[member.user_id];
            try {
              const profile = await requestAgentApplication<{
                protocol: string;
                version: number;
                output_mode: string;
              }>(channelId, member.user_id, 'describe_application');
              if (
                profile.protocol !== 'remote-control' ||
                profile.version !== 1 ||
                (profile.output_mode !== 'normalized' && profile.output_mode !== 'legacy')
              )
                return;
              if (
                socket !== wsRef.current ||
                sessionSubscriptionScopes.current.get(channelId) !== subscription ||
                subscribedSessionOwner(channelId)?.accountId !== owner.accountId
              )
                return;
              const presence = usePresenceStore.getState();
              if (
                presence.providerOutputEpoch !== profileEpoch ||
                (presence.providerOutputRevisions[member.user_id] !== profileRevision &&
                  presence.providerOutputModes[member.user_id] !== profile.output_mode)
              )
                return;
              presence.setProviderOutputMode(member.user_id, profile.output_mode);
              if (profile.output_mode === 'normalized')
                await syncNormalizedActivity(channelId, member.user_id);
            } catch {
              if (
                socket === wsRef.current &&
                sessionSubscriptionScopes.current.get(channelId) === subscription &&
                subscribedSessionOwner(channelId)?.accountId === owner.accountId
              )
                useDshSessionStore
                  .getState()
                  .reportRecovery({ ...owner, channelId, agentId: member.user_id }, 'unavailable');
            }
          }),
      );
    },
    [subscribedSessionOwner],
  );
  useEffect(() => {
    const stop = useChannelMemberStore.subscribe(() => {
      for (const pending of pendingActivityRecovery.current.values()) {
        for (const retry of pending.values()) retry();
      }
    });
    return () => {
      stop();
      pendingActivityRecovery.current.clear();
    };
  }, []);
  useEffect(() => {
    const recoverResolvedSubscriptions = () => {
      if (!authenticatedRef.current || wsRef.current?.readyState !== WebSocket.OPEN) return;
      for (const channelId of subscribedChannels.current) {
        const subscription = sessionSubscriptionScopes.current.get(channelId);
        if (!subscription || subscription.guildId) continue;
        const scope = resolveNormalizedSessionOwner(channelId);
        if (!scope || scope.accountId !== subscription.accountId) continue;
        sessionSubscriptionScopes.current.set(channelId, scope);
        wsRef.current.send(JSON.stringify({ type: 'subscribe', channel_id: channelId }));
      }
    };
    const unsubscribe = [useChannelStore, useChannelMemberStore, useDmStore, useThreadStore].map(
      (store) => store.subscribe(recoverResolvedSubscriptions),
    );
    return () => {
      for (const stop of unsubscribe) stop();
    };
  }, []);
  const dmChannels = useDmStore((s) => s.dmChannels);
  const pendingMessages = useMessageStore((s) => s.pendingMessages);

  const currentChannelIdRef = useRef(useChannelStore.getState().currentChannelId);
  currentChannelIdRef.current = useChannelStore.getState().currentChannelId;

  const findPendingChannelId = useCallback(
    (clientMessageId: string): string | null => {
      const pendingMessages = useMessageStore.getState().pendingMessages;
      for (const [channelId, pending] of Object.entries(pendingMessages)) {
        if (pending.some((p) => p.clientMessageId === clientMessageId)) return channelId;
      }
      return null;
    },
    [pendingMessages],
  );

  const setConnectionState = useCallback((cs: ConnectionState) => {
    // A socket gap retires live controls, while received execution detail stays readable.
    // Task pulses rehydrate from the durable agent status snapshot on reconnect.
    if (cs !== 'connected') {
      useTaskRunStore.getState().clear();
      useNormalizedActivityStore.getState().suspendAll();
      const { currentUser } = useAuthStore.getState();
      if (currentUser) useDshSessionStore.getState().suspendAll(currentUser.id);
    }
    usePresenceStore.getState().setConnectionState(cs);
  }, []);

  /**
   * Resolves the optimistic rows whose sends the gap swallowed the ack for.
   *
   * The rows offered here are only what the re-read newly learned, never the
   * whole window it returned: a send is matched by sender and text, so a message
   * the user had already posted with the same text — and which the store already
   * held — would otherwise claim a pending row that never left the browser, and
   * the retry affordance would vanish with it.
   */
  const reconcilePendingMessages = useCallback(
    (channelId: string, learnedMessages: Message[]) => {
      const pending = useMessageStore.getState().pendingMessages[channelId];
      if (!pending || pending.length === 0) return;
      const learnedContents = new Set(learnedMessages.map((m) => `${m.sender_id}:${m.content}`));
      for (const p of pending) {
        if (learnedContents.has(`${p.senderId}:${p.content}`)) {
          useMessageStore.getState().removePendingMessage(p.clientMessageId, channelId);
          ackTimers.current.get(p.clientMessageId)?.();
          ackTimers.current.delete(p.clientMessageId);
        }
      }
    },
    [pendingMessages],
  );

  const reconcilePendingRef = useRef(reconcilePendingMessages);
  reconcilePendingRef.current = reconcilePendingMessages;

  /**
   * The structural snapshot authorizes subscription reconciliation. Its failure
   * remains visible until an external refresh; independent message/DM repair has
   * a bounded retry policy while this socket remains current.
   */
  const resumeReconnectRepair = useCallback(async (job: ReconnectRepair) => {
    if (job.running) return;
    const isCurrent = () =>
      reconnectRepair.current === job &&
      job.sameContext() &&
      mountedRef.current &&
      wsRef.current === job.ws &&
      job.ws.readyState === WebSocket.OPEN;
    if (!isCurrent()) return;
    job.running = true;
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          if (!(await waitForChannelSnapshot())) return;
        } catch (error) {
          job.sameRevision = captureChannelRevision();
          console.warn('[ws] Project snapshot refresh failed:', error);
          return;
        }
        if (!isCurrent()) return;
        const sameRevision = captureChannelRevision();
        job.sameRevision = sameRevision;
        try {
          const { staleSubscriptions } = await resyncAfterReconnect(
            subscribedChannels.current,
            job.heldAtGapEnd,
            {
              refreshChannels: false,
              listedBefore: job.listedBefore,
              activeServerChannelId: () => activeServerChannelRef.current,
              isCurrent: () => isCurrent() && sameRevision(),
              onMessagesLearned: (channelId, messages) => {
                if (!isCurrent()) return;
                const receipts = job.learnedMessages.get(channelId) ?? new Map<string, Message>();
                for (const message of messages) receipts.set(message.id, message);
                job.learnedMessages.set(channelId, receipts);
              },
            },
          );
          if (!isCurrent()) return;
          if (!sameRevision()) {
            attempt = -1;
            continue;
          }
          for (const channelId of staleSubscriptions) {
            releaseSessionObservationChannel(channelId);
            subscribedChannels.current.delete(channelId);
            sessionSubscriptionScopes.current.delete(channelId);
            fragmentConnection.current?.assembler.clearScope(channelId);
            if (job.ws.readyState === WebSocket.OPEN) {
              job.ws.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
            }
          }
          for (const [channelId, messages] of job.learnedMessages) {
            if (subscribedChannels.current.has(channelId)) {
              reconcilePendingRef.current(channelId, [...messages.values()]);
            }
          }
          await useAgentStore.getState().loadAgents();
          reconnectRepair.current = null;
          return;
        } catch (err) {
          if (!isCurrent()) return;
          if (!sameRevision()) {
            attempt = -1;
            continue;
          }
          const delay = RESYNC_RETRY_DELAYS[attempt];
          if (delay === undefined) {
            console.warn('[ws] reconnect repair failed, giving up:', err);
            return;
          }
          console.warn('[ws] reconnect repair failed, retrying in %dms:', delay, err);
          await new Promise((resolve) => setTimeout(resolve, delay));
          if (!isCurrent()) return;
        }
      }
    } finally {
      job.running = false;
      const state = useChannelStore.getState();
      if (isCurrent() && !job.sameRevision() && !state.loading && !state.loadError) {
        void resumeRepairRef.current?.(job);
      }
    }
  }, []);
  resumeRepairRef.current = resumeReconnectRepair;

  const repairAfterReconnect = useCallback(
    async (ws: WebSocket) => {
      const job: ReconnectRepair = {
        ws,
        heldAtGapEnd: heldMessagesAtGapEnd(),
        listedBefore: new Set([
          ...useChannelStore.getState().channels.map((channel) => channel.id),
          ...useDmStore.getState().dmChannels.map((channel) => channel.id),
        ]),
        learnedMessages: new Map(),
        sameContext: captureChannelContext(),
        sameRevision: captureChannelRevision(),
        running: true,
      };
      reconnectRepair.current = job;
      try {
        await invalidateChannelScopes();
      } catch (error) {
        job.sameRevision = captureChannelRevision();
        console.warn('[ws] Project snapshot refresh failed:', error);
        return;
      } finally {
        job.running = false;
      }
      await resumeReconnectRepair(job);
    },
    [resumeReconnectRepair],
  );

  useEffect(
    () =>
      useChannelStore.subscribe((state) => {
        const job = reconnectRepair.current;
        if (!job || job.running || state.loading || state.loadError || job.sameRevision()) return;
        void resumeReconnectRepair(job);
      }),
    [resumeReconnectRepair],
  );

  const connect = useCallback(() => {
    if (wsRef.current) {
      const rs = wsRef.current.readyState;
      if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const params = new URLSearchParams();
    params.set('agent_requests_version', '1');
    const userId = getDevUserId();
    if (userId) params.set('user_id', userId);
    const url = `${protocol}//${host}/ws${params.toString() ? `?${params}` : ''}`;

    setConnectionState(reconnectAttempt.current > 0 ? 'reconnecting' : 'connecting');

    const ws = new WebSocket(url);
    const socketAccountId = useAuthStore.getState().currentUser?.id ?? null;
    wsRef.current = ws;
    fragmentConnection.current?.dispose();
    const assembler = new NormalizedFragmentAssembler();
    let fragmentsEnabled = false;
    let viewerProof: 'pending' | 'verified' | 'legacy' | 'rejected' = 'pending';
    const deliverSessionFrame = (
      scope: import('../lib/dsh-session-journal').DshSessionScope,
      frame: Record<string, unknown>,
    ) => {
      if (
        wsRef.current !== ws ||
        !authenticatedRef.current ||
        !mountedRef.current ||
        !subscribedSessionOwner(scope.channelId)
      )
        return;
      if (frame.type === 'normalized_session_availability' && validSessionAvailability(frame)) {
        observations.availability(scope, frame);
        return;
      }
      if (
        frame.shape === 'normalized_timeline_replacement' &&
        isSupportedNormalizedVersion(frame.protocol_version) &&
        validTimelineReplacement(frame.event, frame.protocol_version) &&
        typeof frame.channel_id === 'string' &&
        typeof frame.agent_id === 'string' &&
        typeof frame.sequence === 'number' &&
        typeof frame.timeline_epoch === 'string'
      ) {
        void useDshSessionStore
          .getState()
          .replaceTimeline(scope, {
            protocol_version: frame.protocol_version,
            channel_id: frame.channel_id,
            agent_id: frame.agent_id,
            sequence: frame.sequence,
            timeline_epoch: frame.timeline_epoch,
            event: frame.event,
            ...(typeof frame.session_control_binding_id === 'string'
              ? { session_control_binding_id: frame.session_control_binding_id }
              : {}),
          })
          .then(() => {
            if (typeof frame.timeline_epoch === 'string')
              recoverSessionTimeline(scope, frame.timeline_epoch);
          });
      } else if (validTimelineRecoveryResult(frame))
        useDshSessionStore
          .getState()
          .reportTimelineRecovery(scope, frame.timeline_epoch, frame.status);
      else if (frame.shape === 'normalized') {
        const normalized = normalizeActivityFrame({
          ...frame,
          task_id: typeof frame.task_id === 'string' ? frame.task_id : '',
        });
        if (normalized) {
          usePresenceStore.getState().setProviderOutputMode(scope.agentId, 'normalized');
          useDshSessionStore.getState().receive(scope, normalized);
        }
      }
      if (typeof frame.timeline_epoch === 'string')
        recoverSessionTimeline(scope, frame.timeline_epoch);
    };
    const applicationChannels = new Set<string>();
    const applications = bindApplicationRequestSocket(
      ws,
      (channelId) => applicationChannels.has(channelId) && !!subscribedSessionOwner(channelId),
    );
    const observations = bindSessionObservationSocket(ws, applications);
    const interactions = bindInteractionResponseSocket(ws, applications);
    interactionConnection.current = interactions;
    const reportFailures = (failures: NormalizedTransportFailure[]) => {
      if (wsRef.current !== ws || !authenticatedRef.current || !mountedRef.current) return;
      for (const failure of failures) {
        const { channel_id, agent_id, task_id, sequence, protocol_version } = failure;
        if (channel_id && agent_id && failure.timeline_epoch) {
          const report = (scope: import('../lib/dsh-session-journal').DshSessionScope) => {
            useDshSessionStore
              .getState()
              .reportTimelineRecovery(scope, failure.timeline_epoch!, 'failed');
            observations.recover(scope, failure.timeline_epoch!);
          };
          if (failure.session_control_binding_id)
            observations.route(failure as unknown as Record<string, unknown>, (scope) =>
              report(scope),
            );
          else {
            const owner = subscribedSessionOwner(channel_id);
            if (owner) report({ ...owner, channelId: channel_id, agentId: agent_id });
          }
          continue;
        }
        if (
          !channel_id ||
          !agent_id ||
          !task_id ||
          typeof sequence !== 'number' ||
          !Number.isSafeInteger(sequence) ||
          sequence < 1
        )
          continue;
        if (
          [
            [channel_id, 256],
            [task_id, 256],
            [agent_id, 512],
          ].some(
            ([value, limit]) =>
              typeof value !== 'string' ||
              new TextEncoder().encode(value).byteLength > (limit as number),
          )
        )
          continue;
        const owner = subscribedSessionOwner(channel_id);
        if (!owner) continue;
        const local = normalizedTransportError(
          {
            type: 'agent_activity',
            shape: 'normalized',
            channel_id,
            agent_id,
            task_id,
            sequence,
            protocol_version: isSupportedNormalizedVersion(protocol_version)
              ? protocol_version
              : PROTOCOL_VERSION,
            ...(failure.occurred_at !== undefined ? { occurred_at: failure.occurred_at } : {}),
            event: {},
          },
          { reason: failure.reason, eventId: failure.event_id },
        );
        const frame = normalizeActivityFrame(
          local as import('../types/ws-frames').NormalizedActivityFrame,
        );
        if (!frame) continue;
        const reason =
          frame.event.type === 'timeline' && frame.event.item.type === 'error'
            ? frame.event.item.message
            : 'Activity is unavailable.';
        useDshSessionStore
          .getState()
          .receive({ ...owner, channelId: channel_id, agentId: agent_id }, frame, Date.now(), {
            unavailable: true,
            reason,
          });
      }
    };
    const expiryTimer = setInterval(() => reportFailures(assembler.expire()), 1000);
    fragmentConnection.current = {
      assembler,
      dispose: () => {
        clearInterval(expiryTimer);
        assembler.reset();
        fragmentsEnabled = false;
        observations.close();
      },
    };

    ws.onopen = () => {
      if (
        !mountedRef.current ||
        wsRef.current !== ws ||
        socketAccountId !== (useAuthStore.getState().currentUser?.id ?? null)
      )
        return;
      const wasReconnect = reconnectAttempt.current > 0;
      reconnectAttempt.current = 0;
      setConnectionState('connected');

      const selfId = useAuthStore.getState().currentUser?.id;
      if (selfId) {
        usePresenceStore.getState().userOnline(selfId);
        markRT3Presence(selfId, 'online', undefined);
      }

      // Re-subscribe to all channels
      for (const channelId of subscribedChannels.current) {
        ws.send(JSON.stringify({ type: 'subscribe', channel_id: channelId }));
      }

      // A gap in the socket is a gap in the truth: the missed pushes are gone,
      // so the state they would have carried is re-read from the tables that
      // hold it rather than replayed.
      if (wasReconnect) {
        void repairAfterReconnect(ws);
        void reconcileProjectMembership().catch(() => {});
      }

      // Start heartbeat
      if (pingTimer.current) clearInterval(pingTimer.current);
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL);
    };

    ws.onmessage = (event) => {
      if (
        !mountedRef.current ||
        wsRef.current !== ws ||
        socketAccountId !== (useAuthStore.getState().currentUser?.id ?? null)
      )
        return;
      if (viewerProof === 'rejected') return;
      let frame: { type: string; [key: string]: unknown };
      try {
        frame = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!frame || typeof frame !== 'object' || typeof frame.type !== 'string') return;
      if (frame.type === 'connected') {
        fragmentsEnabled = frame.agent_requests_version === 1;

        const actualViewer = typeof frame.viewer_id === 'string' ? frame.viewer_id : null;
        if (socketAccountId && actualViewer && actualViewer !== socketAccountId) {
          viewerProof = 'rejected';
          interactions.close();
          ws.onclose = null;
          ws.close();
          cleanup();
          disconnectMessageCache();
          rejectMessageViewer(socketAccountId, actualViewer);
          return;
        }
        if (viewerProof === 'pending') {
          viewerProof = socketAccountId && actualViewer === socketAccountId ? 'verified' : 'legacy';
          if (viewerProof === 'legacy') disconnectMessageCache();
        }
        applications.enable(frame.agent_requests_version === 1);
        return;
      }
      if (viewerProof === 'pending') {
        viewerProof = 'legacy';
        disconnectMessageCache();
      }
      if (
        frame.type === 'agent_activity' &&
        (frame.event as { type?: unknown } | undefined)?.type === 'remote_transport'
      ) {
        const channelId = typeof frame.channel_id === 'string' ? frame.channel_id : '';
        if (!subscribedSessionOwner(channelId)) return;
        if (fragmentsEnabled) {
          const application = unwrapNormalizedApplicationTransport(frame);
          if (
            application &&
            typeof application === 'object' &&
            'type' in application &&
            ['normalized_activity_sync_result', 'normalized_session_availability'].includes(
              String(application.type),
            )
          )
            frame = application as typeof frame;
        }
      }
      if (frame.type === 'normalized_session_availability') {
        if (observations.supported() && validSessionAvailability(frame))
          observations.route(frame, deliverSessionFrame);
        return;
      }
      if (frame.type === 'normalized_activity_sync_result' && frame.timeline_epoch !== undefined) {
        if (!observations.supported() || !validTimelineRecoveryResult(frame)) return;
        if (observations.route(frame, deliverSessionFrame)) return;
        const owner = subscribedSessionOwner(frame.channel_id);
        if (owner && frame.agent_id) {
          const scope = { ...owner, channelId: frame.channel_id, agentId: frame.agent_id };
          const current = useDshSessionStore.getState().sessions[dshSessionKey(scope)];
          if (current?.sessionControlBindingId === frame.session_control_binding_id)
            deliverSessionFrame(scope, frame);
        }
        return;
      }
      if (
        frame.type === 'subscribed' &&
        typeof frame.channel_id === 'string' &&
        subscribedSessionOwner(frame.channel_id)
      ) {
        applicationChannels.add(frame.channel_id);
        applications.resume(frame.channel_id);
      }
      if (frame.type === 'agent_response') {
        applications.receive(frame);
        return;
      }
      if (frame.type === 'interaction_response_result') {
        interactions.receive(frame);
        return;
      }
      if (frame.type === 'agent_activity') {
        const channelId = typeof frame.channel_id === 'string' ? frame.channel_id : '';
        if (
          (frame.shape === 'normalized_fragment' ||
            frame.shape === 'normalized' ||
            frame.shape === 'normalized_timeline_replacement') &&
          !subscribedSessionOwner(channelId)
        ) {
          assembler.clearScope(channelId);
          return;
        }
        if (
          (frame.shape === 'normalized_fragment' ||
            frame.shape === 'normalized' ||
            frame.shape === 'normalized_timeline_replacement') &&
          !isSupportedNormalizedVersion(frame.protocol_version)
        ) {
          reportFailures([
            { ...(frame as unknown as NormalizedTransportFrame), reason: 'invalid_event' },
          ]);
          return;
        }
        if (
          (frame.shape === 'normalized_fragment' ||
            (frame.event as { type?: unknown } | undefined)?.type === 'remote_transport') &&
          !fragmentsEnabled
        ) {
          assembler.clearScope(channelId);
          reportFailures([
            {
              ...(frame as unknown as NormalizedTransportFrame),
              reason: 'fragmentation_unavailable',
            },
          ]);
          return;
        }
        const result = assembler.receive(frame, new TextEncoder().encode(event.data).byteLength);
        reportFailures(result.failures);
        if (result.status === 'complete') {
          const completed = result.frame as unknown as Record<string, unknown>;
          if (observations.route(completed, deliverSessionFrame)) {
            for (const following of result.following ?? [])
              observations.route(
                following as unknown as Record<string, unknown>,
                deliverSessionFrame,
              );
            return;
          }
          if (result.frame.shape === 'normalized_timeline_replacement') {
            const owner = subscribedSessionOwner(channelId);
            if (owner && typeof result.frame.agent_id === 'string') {
              const scope = { ...owner, channelId, agentId: result.frame.agent_id };
              deliverSessionFrame(scope, completed);
              for (const following of result.following ?? [])
                deliverSessionFrame(scope, following as unknown as Record<string, unknown>);
            }
            return;
          }
          const normalized = normalizeActivityFrame(
            result.frame as import('../types/ws-frames').NormalizedActivityFrame,
          );
          if (!normalized) {
            reportFailures([{ ...result.frame, reason: 'invalid_event' }]);
            return;
          }
          usePresenceStore.getState().setProviderOutputMode(normalized.agent_id, 'normalized');
          handleMessageRef.current(
            result.frame as unknown as { type: string; [key: string]: unknown },
            viewerProof === 'verified',
          );
          return;
        }
        if (
          frame.shape === 'normalized_fragment' ||
          frame.shape === 'normalized' ||
          frame.shape === 'normalized_timeline_replacement'
        )
          return;
      }
      handleMessageRef.current(frame, viewerProof === 'verified');
    };

    ws.onclose = (event) => {
      clearInterval(expiryTimer);
      assembler.reset();
      fragmentsEnabled = false;
      observations.close();
      interactions.close();
      if (
        !mountedRef.current ||
        wsRef.current !== ws ||
        socketAccountId !== (useAuthStore.getState().currentUser?.id ?? null)
      )
        return;
      viewerProof = 'rejected';
      disconnectMessageCache();
      cleanup();
      useServerStore.getState().invalidateMembershipAuthority();

      if (AUTH_FAILURE_CODES.has(event.code)) {
        setConnectionState('disconnected');
        console.warn('[ws] Auth failure (code %d), not reconnecting:', event.code, event.reason);
        onAuthCloseRef.current?.();
        return;
      }

      console.info('[ws] Closed (code %d), scheduling reconnect', event.code);
      scheduleReconnectRef.current?.();
    };

    ws.onerror = () => {
      // onclose will fire after onerror
    };
  }, [
    setConnectionState,
    repairAfterReconnect,
    reconcileProjectMembership,
    subscribedSessionOwner,
  ]);

  const cleanup = useCallback(() => {
    fragmentConnection.current?.dispose();
    fragmentConnection.current = undefined;
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = undefined;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    setConnectionState('reconnecting');
    const delay =
      RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)]!;
    reconnectAttempt.current++;
    reconnectTimer.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }, [connect, setConnectionState]);

  scheduleReconnectRef.current = scheduleReconnect;

  const handleMessage = useCallback(
    (data: { type: string; [key: string]: unknown }, verifiedViewer: boolean) => {
      const coreEffects = reduceRealtimeFrame(data);
      switch (data.type) {
        case 'new_message': {
          const message = coreEffects.find((effect) => effect.kind === 'message.received')
            ?.message as Message | undefined;
          if (!message || !subscribedChannels.current.has(message.channel_id)) break;
          if (verifiedViewer) recordMessageReceipt(message.channel_id, { type: 'new', message });
          onMessageReceived(message.channel_id, message);
          break;
        }
        case 'thread_create': {
          // A cached collection needs the whole lifecycle, not just updates. A
          // thread born after loadThreads() took its snapshot would otherwise
          // never enter the store, and every later count update for it would
          // land on nothing.
          const thread = data.thread as
            | (Channel & {
                parent_id?: string;
                message_id?: string;
                message_count?: number;
              })
            | undefined;
          if (
            thread?.id &&
            thread.parent_id &&
            thread.message_id &&
            subscribedChannels.current.has(thread.parent_id)
          ) {
            useThreadStore.getState().upsertThread({
              id: thread.id,
              message_id: thread.message_id,
              parent_channel_id: thread.parent_id,
              message_count: thread.message_count ?? 0,
            });
          }
          break;
        }
        case 'thread_updated': {
          // The frame identifies the thread completely, so a count for a thread
          // whose creation event was missed still renders instead of being
          // dropped — the same call serves both cases.
          const threadId = data.thread_id as string;
          const parentChannelId = data.channel_id as string | undefined;
          const originMessageId = data.message_id as string | undefined;
          if (
            threadId &&
            parentChannelId &&
            originMessageId &&
            subscribedChannels.current.has(parentChannelId)
          ) {
            useThreadStore.getState().upsertThread({
              id: threadId,
              message_id: originMessageId,
              parent_channel_id: parentChannelId,
              message_count: data.message_count as number,
            });
          }
          break;
        }
        case 'task_updated':
        case 'task_create': {
          const task = data.task as any;
          const activeGuildId = useAuthStore.getState().guildId;
          const knownChannel = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === task?.channel_id && channel.guild_id === activeGuildId,
            );
          if (
            task?.id &&
            task.guild_id === activeGuildId &&
            knownChannel &&
            subscribedChannels.current.has(task.channel_id)
          ) {
            useTaskStore.getState().upsertTask(task);
          }
          break;
        }
        case 'task_deleted': {
          const taskId = data.task_id as string;
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const knownChannel = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (
            taskId &&
            data.guild_id === activeGuildId &&
            knownChannel &&
            subscribedChannels.current.has(channelId)
          ) {
            useTaskStore.getState().removeTask(taskId);
          }
          break;
        }
        case 'agent_task_state_changed': {
          const frame = data as unknown as AgentTaskStateChangedFrame;
          if (frame.task_id) {
            // The hub already scoped delivery to the subscribed task channel.
            useTaskRunStore
              .getState()
              .setTaskRun(frame.task_id, frame.agent_id, frame.state === 'busy');
          }
          break;
        }
        case 'subscribed': {
          if (typeof data.channel_id === 'string' && subscribedSessionOwner(data.channel_id)) {
            resumeSessionObservationChannel(data.channel_id);
            void syncSubscribedActivity(data.channel_id);
          }
          break;
        }
        case 'presence': {
          const userId = data.user_id as string;
          const status = data.status as string;
          if (status === 'online') {
            usePresenceStore.getState().userOnline(userId);
          } else {
            usePresenceStore.getState().userOffline(userId);
          }
          if (status === 'online' || status === 'offline') {
            usePresenceStore.getState().setProviderOutputMode(userId, null);
            useNormalizedActivityStore.getState().suspendAgent(userId);
            const { currentUser } = useAuthStore.getState();
            if (currentUser)
              useDshSessionStore.getState().suspendAgent(currentUser.id, undefined, userId);
            if (status === 'online' && wsRef.current?.readyState === WebSocket.OPEN) {
              for (const channelId of subscribedChannels.current) {
                if (subscribedSessionOwner(channelId))
                  void syncSubscribedActivity(channelId, userId);
              }
            }
          }
          // Server emits the 'presence' frame for every WS user (humans + agents
          // share /ws). The agent presence cache read by usePresence() only fills
          // from 'presence.changed' frames, and the server currently does not emit
          // those, so without this mirror agents show "已离线" even while their
          // runtime is connected. Humans currently have no usePresence(humanId)
          // consumers, so writing them into the cache is a harmless no-op. Only
          // online/offline come through this path;
          // error / busy / idle remain on the 'presence.changed' frame.
          if (status === 'online' || status === 'offline') {
            markPresence(userId, status, undefined);
            // Feed the human multi-device presence store so member rows reflect
            // real presence instead of being permanently offline. The server
            // broadcasts this `presence` frame for every WS user on connect /
            // disconnect (server-go internal/ws/client.go), so online/offline here
            // is the in-scope data source. The four-state away/thinking fanout is
            // not emitted today; away is derived from last-seen inside
            // useRT3Presence, and thinking has no server frame yet.
            markRT3Presence(userId, status, undefined);
          }
          break;
        }
        // Agent runtime presence carries {agent_id, status, reason?}. Do not
        // accept IP / last_heartbeat_at / connection_count; the server strips
        // them. 5s throttling is enforced on server egress and again at the
        // client markPresence entry point.
        // Busy/idle is not part of this frame; it arrives on the
        // separate 'agent_task_state_changed' frame (see server
        // packages/server-go/internal/ws/agent_task_state_changed_frame.go).
        case 'presence.changed': {
          const agentID = data.agent_id as string | undefined;
          const status = data.status as AgentRuntimeState | undefined;
          const reason = data.reason as AgentRuntimeReason | undefined;
          if (agentID && (status === 'online' || status === 'offline' || status === 'error')) {
            markPresence(agentID, status, reason);
            useNormalizedActivityStore.getState().suspendAgent(agentID);
            const { currentUser } = useAuthStore.getState();
            if (currentUser)
              useDshSessionStore.getState().suspendAgent(currentUser.id, undefined, agentID);
            if (status === 'online' && wsRef.current?.readyState === WebSocket.OPEN) {
              for (const channelId of subscribedChannels.current) {
                if (subscribedSessionOwner(channelId))
                  void syncSubscribedActivity(channelId, agentID);
              }
            }
          }
          break;
        }
        case 'guilds_changed': {
          void invalidateChannelScopes().catch(() => {});
          void reconcileProjectMembership().catch(() => {});
          break;
        }
        case 'guild_members_changed': {
          const frame = data as unknown as GuildMembersChangedFrame;
          if (typeof frame.guild_id === 'string' && frame.guild_id) {
            void reconcileProjectMembership().catch(() => {});
          }
          break;
        }
        case 'guild_removed': {
          const guildId = data.guild_id as string | undefined;
          if (guildId)
            void useChannelStore
              .getState()
              .invalidateChannels(guildId)
              .catch(() => {});
          if (guildId && guildId === useAuthStore.getState().guildId) {
            window.dispatchEvent(
              new CustomEvent('guild_membership_removed', { detail: { guildId } }),
            );
          }
          void useServerStore
            .getState()
            .refreshGuilds()
            .catch(() => {});
          break;
        }
        case 'guild_invites_changed': {
          window.dispatchEvent(new CustomEvent('guild_invites_changed', { detail: data }));
          break;
        }
        case 'zones_changed': {
          const frame = data as unknown as ZonesChangedFrame;
          if (typeof frame.guild_id === 'string' && frame.guild_id) {
            void useChannelStore
              .getState()
              .invalidateChannels(frame.guild_id)
              .catch(() => {});
          }
          break;
        }
        case 'channel_created': {
          // Creation carries the stored row, not this recipient's membership or unread projection.
          const channel = data.channel as { guild_id?: unknown } | undefined;
          if (typeof channel?.guild_id === 'string' && channel.guild_id) {
            void useChannelStore
              .getState()
              .invalidateChannels(channel.guild_id)
              .catch(() => {});
          }
          break;
        }
        case 'channel_added': {
          const channel = data.channel as Channel | undefined;
          if (channel?.id && channel.guild_id) {
            useChannelStore.getState().addChannel(channel);
          }
          break;
        }
        case 'channel_removed':
        case 'channel_deleted': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (!channelId || typeof data.guild_id !== 'string') break;
          if (data.guild_id !== activeGuildId || !known) {
            void useChannelStore
              .getState()
              .invalidateChannels(data.guild_id)
              .catch(() => {});
            break;
          }
          useMessageStore.getState().purgeChannels([channelId]);
          useChannelStore.getState().removeChannel(channelId);
          useNormalizedActivityStore.getState().clearChannel(channelId);
          const accountId = useAuthStore.getState().currentUser?.id;
          if (accountId && activeGuildId)
            useDshSessionStore.getState().unloadChannel(accountId, activeGuildId, channelId);
          releaseSessionObservationChannel(channelId);
          subscribedChannels.current.delete(channelId);
          sessionSubscriptionScopes.current.delete(channelId);
          fragmentConnection.current?.assembler.clearScope(channelId);
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
          }
          if (data.type === 'channel_deleted' && currentChannelIdRef.current === channelId) {
            const name = data.name as string | undefined;
            showToast(name ? msg`Channel #${name} was deleted.` : msg`The channel was deleted.`);
          }
          break;
        }
        case 'channel_move_source_evict': {
          const sourceGuildId = data.source_guild_id as string | undefined;
          if (!sourceGuildId) break;
          if (sourceGuildId !== useAuthStore.getState().guildId) {
            void useChannelStore
              .getState()
              .invalidateChannels(sourceGuildId)
              .catch(() => {});
            break;
          }
          const subtree = Array.isArray(data.subtree_channel_ids)
            ? data.subtree_channel_ids.filter((id): id is string => typeof id === 'string')
            : [];
          if (subtree.length === 0) break;
          for (const channelId of subtree) {
            releaseSessionObservationChannel(channelId);
            subscribedChannels.current.delete(channelId);
            sessionSubscriptionScopes.current.delete(channelId);
            fragmentConnection.current?.assembler.clearScope(channelId);
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
            }
            useTypingStore.getState().clearChannel(channelId);
            useChannelMemberStore.getState().clearChannel(channelId);
            useAgentActivityStore.getState().clearChannel(channelId);
            useNormalizedActivityStore.getState().clearChannel(channelId);
            const accountId = useAuthStore.getState().currentUser?.id;
            if (accountId && sourceGuildId)
              useDshSessionStore.getState().unloadChannel(accountId, sourceGuildId, channelId);
          }
          useChannelStore.getState().purgeSubtree(subtree);
          useMessageStore.getState().purgeChannels(subtree);
          useThreadStore.getState().purgeChannels(subtree);
          useTaskStore.getState().purgeChannels(subtree);
          break;
        }
        case 'channel_move_target_refresh': {
          const targetGuildId = data.target_guild_id as string | undefined;
          if (targetGuildId) {
            void useChannelStore
              .getState()
              .invalidateChannels(targetGuildId)
              .catch(() => {});
            if (targetGuildId === useAuthStore.getState().guildId) {
              void useTaskStore.getState().loadGuildTasks(targetGuildId);
            }
          }
          break;
        }
        case 'visibility_changed': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (data.guild_id === activeGuildId && known) {
            useChannelStore.getState().updateChannel(channelId, {
              visibility: data.visibility as 'public' | 'private',
            });
          } else if (typeof data.guild_id === 'string') {
            void useChannelStore
              .getState()
              .invalidateChannels(data.guild_id)
              .catch(() => {});
          }
          break;
        }
        case 'channel_updated': {
          const channel = data.channel as Channel | undefined;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (candidate) => candidate.id === channel?.id && candidate.guild_id === activeGuildId,
            );
          if (channel?.id && channel.guild_id === activeGuildId && known) {
            useChannelStore.getState().updateChannel(channel.id, channel);
          } else if (channel?.guild_id) {
            void useChannelStore
              .getState()
              .invalidateChannels(channel.guild_id)
              .catch(() => {});
          }
          break;
        }
        case 'channels_reordered': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (data.guild_id !== activeGuildId || !known) {
            if (typeof data.guild_id === 'string') {
              void useChannelStore
                .getState()
                .invalidateChannels(data.guild_id)
                .catch(() => {});
            }
            break;
          }
          const updates: Partial<Channel> = {};
          if (typeof data.position === 'string') updates.position = data.position;
          if (updates.position !== undefined)
            useChannelStore.getState().updateChannel(channelId, updates);
          else
            void useChannelStore
              .getState()
              .invalidateChannels(activeGuildId!)
              .catch(() => {});
          break;
        }
        case 'user_joined': {
          const joinedChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(joinedChannelId)) break;
          const joinedUserId = data.user_id as string;
          if (joinedUserId) {
            usePresenceStore.getState().userOnline(joinedUserId);
          }
          if (joinedChannelId) {
            if (typeof data.member_count === 'number') {
              useChannelStore
                .getState()
                .updateChannel(joinedChannelId, { member_count: data.member_count as number });
            } else if (
              useChannelStore.getState().channels.some((channel) => channel.id === joinedChannelId)
            ) {
              void useChannelStore
                .getState()
                .invalidateChannels()
                .catch(() => {});
            }
            useChannelStore.getState().bumpChannelMembersVersion(joinedChannelId);
          }
          break;
        }
        case 'user_left': {
          const leftChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(leftChannelId)) break;
          if (leftChannelId) {
            if (typeof data.member_count === 'number') {
              useChannelStore
                .getState()
                .updateChannel(leftChannelId, { member_count: data.member_count as number });
            } else if (
              useChannelStore.getState().channels.some((channel) => channel.id === leftChannelId)
            ) {
              void useChannelStore
                .getState()
                .invalidateChannels()
                .catch(() => {});
            }
            useChannelStore.getState().bumpChannelMembersVersion(leftChannelId);
          }
          break;
        }
        case 'pong':
          break;
        case 'typing': {
          const channelId = data.channel_id as string;
          if (!subscribedChannels.current.has(channelId)) break;
          const userId = data.user_id as string;
          const displayName = data.display_name as string;
          useTypingStore.getState().setTyping(channelId, userId, displayName);
          break;
        }
        case 'reaction_update': {
          const channelId = typeof data.channel_id === 'string' ? data.channel_id : '';
          if (channelId && !subscribedChannels.current.has(channelId)) break;
          const messageId = data.message_id as string;
          const reactions = data.reactions as NonNullable<Message['reactions']>;
          if (verifiedViewer) {
            recordMessageReceipt(channelId, { type: 'reactions', messageId, reactions });
          }
          const store = useMessageStore.getState();
          const channelIds = channelId
            ? [channelId]
            : Object.entries(store.messages)
                .filter(
                  ([id, messages]) =>
                    subscribedChannels.current.has(id) &&
                    messages.some((message) => message.id === messageId),
                )
                .map(([id]) => id);
          for (const id of channelIds) store.updateReactions(id, messageId, reactions);
          break;
        }
        case 'message_ack': {
          const effect = coreEffects.find((candidate) => candidate.kind === 'message.acknowledged');
          const clientMessageId =
            effect?.kind === 'message.acknowledged' ? effect.clientMessageId : null;
          const serverMessage =
            effect?.kind === 'message.acknowledged'
              ? (effect.message as unknown as Message)
              : undefined;
          if (clientMessageId && serverMessage) {
            if (verifiedViewer)
              recordMessageReceipt(serverMessage.channel_id, {
                type: 'ack',
                message: serverMessage,
              });
            onMessageAcked(serverMessage.channel_id, clientMessageId, serverMessage);

            ackTimers.current.get(clientMessageId)?.();
            ackTimers.current.delete(clientMessageId);
          }
          break;
        }
        case 'message_nack': {
          const nackEffect = coreEffects.find((effect) => effect.kind === 'message.rejected');
          const nackClientId =
            nackEffect?.kind === 'message.rejected' ? nackEffect.clientMessageId : null;
          if (nackClientId) {
            const channelId = findPendingChannelId(nackClientId);
            if (channelId) {
              useMessageStore.getState().failPendingMessage(nackClientId, channelId);
            }
            ackTimers.current.get(nackClientId)?.();
            ackTimers.current.delete(nackClientId);
          }
          break;
        }
        case 'message_sent':
          break;
        case 'message_edited': {
          const editedMsg = data.message as Message;
          if (!subscribedChannels.current.has(editedMsg.channel_id)) break;
          if (verifiedViewer)
            recordMessageReceipt(editedMsg.channel_id, { type: 'edit', message: editedMsg });
          useMessageStore.getState().editMessage(editedMsg.channel_id, editedMsg);
          break;
        }
        case 'message_deleted': {
          const deletedChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(deletedChannelId)) break;
          const deletedMessageId = data.message_id as string;
          const deletedAt = data.deleted_at as number;
          if (verifiedViewer)
            recordMessageReceipt(deletedChannelId, {
              type: 'delete',
              messageId: deletedMessageId,
              deletedAt,
            });
          useMessageStore.getState().deleteMessage(deletedChannelId, deletedMessageId, deletedAt);
          break;
        }
        case 'commands_updated': {
          window.dispatchEvent(new CustomEvent('commands_updated'));
          break;
        }
        case 'agent_invitation_pending': {
          // Bridge the owner-side push to a window CustomEvent so InvitationsInbox /
          // Sidebar can subscribe without coupling to this hook.
          // Schema lock: docs/blueprint/realtime.md §2.3 (exact BPP schema match).
          dispatchInvitationPending(data as unknown as AgentInvitationPendingFrame);
          break;
        }
        case 'agent_invitation_decided': {
          // Synchronize approve/reject/expire across the user's clients.
          dispatchInvitationDecided(data as unknown as AgentInvitationDecidedFrame);
          break;
        }
        case 'mention_pushed': {
          // The frame signals that the current user was mentioned in a message.
          // Its envelope is signal-only;
          // MessageList listens via useMentionPushed and refreshes the
          // channel via actions.loadMessages. body_preview is server-truncated
          // to 80 runes; the client must not re-parse it (server data-trim constraint,
          // full body arrives via the existing message backfill path).
          // mention_pushed_frame.go::MentionPushedFrame defines the exact wire shape.
          dispatchMentionPushed(data as unknown as MentionPushedFrame);
          break;
        }
        case 'execution_usage_updated': {
          const channelId = typeof data.channel_id === 'string' ? data.channel_id : '';
          if (channelId && subscribedChannels.current.has(channelId)) {
            notifyExecutionUsageUpdated(channelId);
          }
          break;
        }
        case 'normalized_activity_sync_result': {
          const channelId = data.channel_id as string;
          const owner = subscribedSessionOwner(channelId);
          if (
            owner &&
            typeof data.agent_id === 'string' &&
            (data.status === 'unavailable' || data.status === 'failed')
          ) {
            useDshSessionStore
              .getState()
              .reportRecovery({ ...owner, channelId, agentId: data.agent_id }, data.status);
          }
          break;
        }
        case 'agent_activity': {
          // Channel delivery is the only source of session detail; there is no history pull.
          const frame = data as unknown as AgentActivityFrame;
          if (subscribedChannels.current.has(frame.channel_id)) {
            if (frame.shape === 'normalized') {
              const receivedAt = Date.now();
              const owner = subscribedSessionOwner(frame.channel_id);
              if (owner) {
                useDshSessionStore.getState().receive(
                  {
                    ...owner,
                    channelId: frame.channel_id,
                    agentId: frame.agent_id,
                  },
                  frame as import('../types/ws-frames').NormalizedActivityFrame,
                  receivedAt,
                );
              }
            } else {
              useAgentActivityStore.getState().applyFrame(frame);
            }
          }
          break;
        }
        case 'error':
          console.warn('[ws] Server error:', data.message);
          break;
      }
    },
    [
      showToast,
      findPendingChannelId,
      reconcileProjectMembership,
      subscribedSessionOwner,
      syncSubscribedActivity,
    ],
  );

  handleMessageRef.current = handleMessage;

  const subscribe = useCallback((channelId: string) => {
    if (subscribedChannels.current.has(channelId)) return;
    const { currentUser } = useAuthStore.getState();
    if (authenticatedRef.current && currentUser) {
      sessionSubscriptionScopes.current.set(
        channelId,
        resolveNormalizedSessionOwner(channelId) ?? { accountId: currentUser.id },
      );
    }
    subscribedChannels.current.add(channelId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', channel_id: channelId }));
    }
  }, []);

  const unsubscribe = useCallback((channelId: string) => {
    pendingActivityRecovery.current.delete(channelId);
    releaseSessionObservationChannel(channelId);
    subscribedChannels.current.delete(channelId);
    sessionSubscriptionScopes.current.delete(channelId);
    fragmentConnection.current?.assembler.clearScope(channelId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
    }
  }, []);

  // DMs and their threads belong to the account; other subscriptions are authorized
  // by the active Server (including thread channel ids that do not appear in
  // the Server's channel listing). A context switch drops that whole set in
  // one synchronous pass so reconnect cannot resurrect the previous Server.
  const unsubscribeServerChannels = useCallback(() => {
    for (const channelId of [...subscribedChannels.current]) {
      const owner =
        sessionSubscriptionScopes.current.get(channelId) ??
        resolveNormalizedSessionOwner(channelId);
      if (owner?.guildId && isDirectMessageSessionScope({ ...owner, guildId: owner.guildId }))
        continue;
      releaseSessionObservationChannel(channelId);
      subscribedChannels.current.delete(channelId);
      sessionSubscriptionScopes.current.delete(channelId);
      fragmentConnection.current?.assembler.clearScope(channelId);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (previousAccountId.current !== currentAccountId) {
      subscribedChannels.current.clear();
      sessionSubscriptionScopes.current.clear();
      reconnectAttempt.current = 0;
      previousAccountId.current = currentAccountId;
    }
    if (!isAuthenticated) {
      useDshSessionStore.getState().resetMemory();
      // Subscription intent belongs to the authenticated account. Retaining it
      // here would make the next account resubscribe to the previous account's
      // channels before its own bootstrap establishes a context.
      subscribedChannels.current.clear();
      sessionSubscriptionScopes.current.clear();
      return () => {
        mountedRef.current = false;
      };
    }
    connect();

    return () => {
      mountedRef.current = false;
      disconnectMessageCache();
      reconnectRepair.current = null;
      interactionConnection.current?.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      cleanup();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, currentAccountId, connect, cleanup]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') {
        void invalidateChannelScopes().catch(() => {});
        void reconcileProjectMembership().catch(() => {});
      }
    };
    const restorePage = (event: PageTransitionEvent) => {
      if (event.persisted) refreshVisible();
    };
    document.addEventListener('visibilitychange', refreshVisible);
    window.addEventListener('pageshow', restorePage);
    return () => {
      document.removeEventListener('visibilitychange', refreshVisible);
      window.removeEventListener('pageshow', restorePage);
    };
  }, [isAuthenticated, reconcileProjectMembership]);

  // Auto-subscribe to DM channels
  useEffect(() => {
    if (!isAuthenticated) return;
    for (const dm of dmChannels) {
      if (!subscribedChannels.current.has(dm.id)) {
        subscribe(dm.id);
      }
    }
  }, [isAuthenticated, dmChannels, subscribe]);

  const sendWsMessage = useCallback((payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  const registerAckTimer = useCallback((clientMessageId: string, cancel: () => void) => {
    ackTimers.current.set(clientMessageId, cancel);
  }, []);

  return {
    subscribe,
    unsubscribe,
    unsubscribeServerChannels,
    sendWsMessage,
    registerAckTimer,
    connectionState,
  };
}
